import { ArrowBackOutlined } from "@mui/icons-material";
import { Box, Button, Paper, Stack, Typography } from "@mui/material";
import { ReactNode } from "react";
import { useTranslation } from "react-i18next";

interface PageHeaderProps {
  title: string;
  description?: string;
  eyebrow?: string;
  backLabel?: string;
  onBack?: () => void;
  actions?: ReactNode;
  compact?: boolean;
}

/**
 * En-tête commun des écrans fonctionnels.
 *
 * Le retour reste toujours à gauche, le contexte au centre et les actions à
 * droite. Sur mobile, les trois zones sont empilées sans changer leur ordre.
 */
export function PageHeader({
  title,
  description,
  eyebrow,
  backLabel,
  onBack,
  actions,
  compact = false
}: PageHeaderProps) {
  const { t } = useTranslation();
  const resolvedBackLabel = backLabel ?? t("common.home");
  return (
    <Paper
      component="header"
      variant="outlined"
      sx={{
        mb: compact ? 0 : 3,
        p: compact ? { xs: 1.25, md: 1.5 } : { xs: 2, md: 2.5 },
        borderColor: "divider",
        borderTop: 4,
        borderTopColor: "secondary.main"
      }}
    >
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={compact ? { xs: 1, md: 2 } : { xs: 2, md: 3 }}
        alignItems={{ md: "center" }}
      >
        {onBack && (
          <Button
            type="button"
            variant="text"
            startIcon={<ArrowBackOutlined />}
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              onBack();
            }}
            sx={{ alignSelf: { xs: "flex-start", md: "center" }, flexShrink: 0 }}
          >
            {resolvedBackLabel}
          </Button>
        )}

        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          {eyebrow && (
            <Typography
              variant="overline"
              color="secondary.dark"
              sx={{ display: "block", fontWeight: 800, lineHeight: 1.5 }}
            >
              {eyebrow}
            </Typography>
          )}
          <Typography variant={compact ? "h5" : "h4"}
            component="h1" fontWeight={800}>
            {title}
          </Typography>
          {description && (
            <Typography color="text.secondary" sx={{ mt: 0.5, maxWidth: 760 }}>
              {description}
            </Typography>
          )}
        </Box>

        {actions && (
          <Stack
            component="nav"
            aria-label={t("common.pageActions")}
            direction={{ xs: "column", sm: "row" }}
            spacing={1}
            sx={{
              flexShrink: 0,
              width: { xs: "100%", md: "auto" },
              "& .MuiButton-root": { width: { xs: "100%", sm: "auto" } }
            }}
          >
            {actions}
          </Stack>
        )}
      </Stack>
    </Paper>
  );
}
